import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  BoxProps,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";
import GroupIcon from "../../Assets/images/svg/Icons/Group 578.svg";
import "../../Components/Common/Styles/Formstyles.css";
import photo from "../../Assets/images/svg/Icons/file-image-svgrepo-com.svg";

interface CategoryPhotosFormProps {
  formData: any;
  setFormData: (data: any) => void;
  title?: string;
  type?: "banner" | "photo";
  handleCancel?: () => void;
}

const UploadBox = styled(Box)<BoxProps>({
  border: "2px dashed #ccc",
  borderRadius: "10px",
  padding: "20px",
  textAlign: "center",
  cursor: "pointer",
  width: "100%",
  height: 150,
});

const ImagePreview = styled("img")({
  width: "80px",
  height: "80px",
  borderRadius: "8px",
  objectFit: "cover",
});

const CategoryPhotosForm: React.FC<CategoryPhotosFormProps> = ({
  formData,
  setFormData,
  title = "Category Images",
  type,
  handleCancel,
}) => {
  const [photos, setPhotos] = useState<(File | string)[]>(formData.photos || []);
  const [fileURLs, setFileURLs] = useState<string[]>([]);

  // Sync with formData.photos when it changes (important for edit mode)
  useEffect(() => {
    setPhotos(formData.photos || []);
  }, [formData.photos]);

  // Generate object URLs for File objects
  useEffect(() => {
    const newURLs = photos.map((item) =>
      item instanceof File ? URL.createObjectURL(item) : item
    );
    setFileURLs(newURLs);

    // Cleanup URLs on unmount
    return () => {
      newURLs.forEach((url, idx) => {
        if (photos[idx] instanceof File) URL.revokeObjectURL(url);
      });
    };
  }, [photos]);

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const updatedPhotos = [...photos, ...fileArray];
      setPhotos(updatedPhotos);
      setFormData({ ...formData, photos: updatedPhotos });
    }
  };

  const handleRemovePhoto = (index: number) => {
    const updatedPhotos = photos.filter((_, i) => i !== index);
    setPhotos(updatedPhotos);
    setFormData({ ...formData, photos: updatedPhotos });
  };

  return (
    <Box>
      {/* Section Title */}
      <Typography className="form-input" mt={3}>
        {title}
      </Typography>

      {/* Upload Box */}
      <UploadBox component="label">
        <img src={photo} alt="photoimage" style={{ marginTop: 15, width: 40 }} />
        <Typography fontSize="14px" mt={1} fontFamily="Poppins">
          Drag and drop photos or{" "}
          <span style={{ color: "#3E93E8", cursor: "pointer" }}>Choose file</span>
        </Typography>
        <input
          hidden
          type="file"
          name="files"
          multiple
          accept="image/*"
          onChange={handlePhotoUpload}
        />
      </UploadBox>

      {/* Preview photos */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={3} mt={2}>
        {fileURLs.map((url, index) => (
          <Box key={index} position="relative" display="inline-block">
            <ImagePreview src={url} alt={`photo-${index}`} />
            <IconButton
              size="small"
              onClick={() => handleRemovePhoto(index)}
              sx={{
                position: "absolute",
                top: "-10px",
                right: "-10px",
                backgroundColor: "white",
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      {/* Cancel button if provided */}
      <Box display="flex" justifyContent="start" gap={2}>
        {handleCancel && (
          <Button onClick={handleCancel} className="btn-cancel">
            <Box component="img" src={GroupIcon} sx={{ mr: 1 }} />
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CategoryPhotosForm;
